<template>
  <div class="profiler">
    
    <div class="profiler__content">
      <iframe
        id="perfetto-iframe"
        class="perfetto-iframe"
        src="/portfolio/index.html"
        title="Profiler"
        sandbox="allow-same-origin allow-scripts"
      />
      <!-- Logo cover overlay -->
      <div class="logo-cover" />
      
      <!-- Loading Dialog -->
      <div v-if="isLoading" class="loading-overlay">
        <div class="loading-dialog">
          <div class="loading-spinner" />
          <p class="loading-text">Loading Trace File</p>
        </div>
      </div>

      <!-- Error Dialog -->
      <div v-if="errorMessage" class="error-overlay">
        <div class="error-dialog">
          <p class="error-text">{{ errorMessage }}</p>
          <button class="error-close-btn" @click="errorMessage = ''">Dismiss</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';

defineOptions({ name: 'Profiler' });

const $toast = useToast();

const props = defineProps({
  primaryColor: {
    type: String,
    default: '#1976d2',
  },
  backgroundColor: {
    type: String,
    default: '#fafafa',
  },
  taskId: {
    type: Number,
    default: null,
  },
  traceFile: {
    type: String,
    default: null,
  },
});

const uid = ref('bt-123');

const fileInput = ref(null);

const isLoading = ref(false);

const errorMessage = ref('');

const isAttemptingLoad = ref(false);

const traceBuffer = ref(null);

const triggerFileInput = () => {
  fileInput.value?.click();
};


const fetchTraceFile = async (taskId, fileName) => {
  try {
    console.log('📥 Fetching trace file:', fileName);
    const dataConnect = new DatastoreConnect();
    
    const response = await dataConnect.api('task.data.download', {
      task_id: taskId,
      folder: 'trace',
      file: fileName,
    });
    
    if (!response) {
      throw new Error('Failed to fetch trace file');
    }
    
    // Convert response to ArrayBuffer
    let buffer = response;
    
    if (typeof buffer === 'object' && buffer !== null) {
      // If it's a JSON object, convert to string then to buffer
      const jsonString = JSON.stringify(buffer);
      const encoder = new TextEncoder();
      const uint8Array = encoder.encode(jsonString);
      buffer = uint8Array.buffer;
    } else if (typeof buffer === 'string') {
      // If it's a base64 string, decode it
      const binaryString = atob(buffer);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      buffer = bytes.buffer;
    }
    
    console.log('✅ Trace file fetched, size:', buffer.byteLength, 'bytes');
    return buffer;
  } catch (error) {
    console.error('❌ Error fetching trace file:', error);
    const errorMsg = error.message || 'Failed to load trace file';
    // Show error toast
    $toast.open({
      queue: true,
      duration: 5000,
      message: errorMsg,
      type: 'error',
      position: 'top'
    });
    throw error;
  }
};

const sendTraceBufferToIframe = async (traceBuffer, fileName = 'trace.perfetto-trace') => {
  try {
    console.log('📤 Sending trace buffer to Perfetto, size:', traceBuffer.byteLength, 'bytes');
    
    const iframe = document.getElementById('perfetto-iframe');
    const contentWindow = iframe?.contentWindow;
    
    if (!contentWindow) {
      throw new Error('Perfetto iframe contentWindow not found');
    }
    
    // Post message with trace buffer
    console.log('📡 Posting trace buffer via postMessage');
    contentWindow.postMessage(
      {
        perfetto: {
          buffer: traceBuffer,
          title: fileName,
          fileName: fileName,
        },
      },
      '*'
    );
    
    console.log('✅ Trace buffer sent to Perfetto via postMessage');
  } catch (error) {
    console.error('❌ Error sending trace buffer to iframe:', error);
    throw error;
  }
};


onMounted(async () => {
  console.log('📊 Profiler component mounted - ready for trace loading');
});

// Watch for changes to traceFile prop (fires immediately on mount if traceFile is set)
watch(
  () => props.traceFile,
  async (newTraceFile) => {
    if (newTraceFile && props.taskId) {
      // Prevent concurrent load attempts
      if (isAttemptingLoad.value) return;
      isAttemptingLoad.value = true;
      
      errorMessage.value = '';
      isLoading.value = true;
      try {
        console.log('📥 Loading trace file:', newTraceFile);
        
        // Fetch the trace file
        const buffer = await fetchTraceFile(props.taskId, newTraceFile);
        
        // Wait for Perfetto to initialize
        console.log('⏳ Waiting for Perfetto to initialize (5 seconds)...');
        await new Promise(resolve => setTimeout(resolve, 5000));
        
        // Send buffer to Perfetto iframe
        await sendTraceBufferToIframe(buffer, newTraceFile);
        
        // Wait for Perfetto to render
        console.log('⏳ Waiting for trace to render in Perfetto...');
        await new Promise(resolve => setTimeout(resolve, 3000));
      } catch (error) {
        console.error('❌ Error loading trace:', error);
        errorMessage.value = error.message || 'Failed to load trace file';
      } finally {
        isLoading.value = false;
        isAttemptingLoad.value = false;
      }
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
.profiler {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  background-color: v-bind(backgroundColor);

  &__header {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 16px 20px;
    background-color: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
    gap: 16px;
  }

  &__controls {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  &__hidden-input {
    display: none;
  }

  &__button {
    padding: 8px 16px;
    background-color: v-bind(primaryColor);
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 0.95rem;
    cursor: pointer;
    transition: filter 0.3s ease;
    white-space: nowrap;

    &:hover {
      filter: brightness(0.9);
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    overflow: hidden;
    position: relative;
  }

  .perfetto-iframe {
    flex: 1 1 auto;
    width: 100%;
    height: 100%;
    border: none;
    overflow: hidden;
  }

  .logo-cover {
    position: absolute;
    top: 0;
    left: 0;
    width: 120px;
    height: 50px;
    background-color: inherit;
    z-index: 10;
    pointer-events: none;
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 100;
  }

  .loading-dialog {
    background-color: white;
    border-radius: 8px;
    padding: 32px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
    min-width: 300px;
  }

  .loading-spinner {
    width: 50px;
    height: 50px;
    border: 4px solid #e0e0e0;
    border-top-color: v-bind(primaryColor);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .loading-text {
    font-size: 16px;
    font-weight: 500;
    color: #333;
    margin: 0;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .error-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 100;
  }

  .error-dialog {
    background-color: #ffebee;
    border: 2px solid #f44336;
    border-radius: 8px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    box-shadow: 0 4px 16px rgba(244, 67, 54, 0.3);
    min-width: 320px;
    max-width: 500px;
  }

  .error-text {
    font-size: 16px;
    font-weight: 500;
    color: #c62828;
    margin: 0;
    text-align: center;
    line-height: 1.5;
  }

  .error-close-btn {
    padding: 8px 24px;
    background-color: #f44336;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: filter 0.3s ease;
    white-space: nowrap;

    &:hover {
      filter: brightness(0.9);
    }
  }
}
</style>
